import { instanceKey } from "@/lib/instanceKey";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { instanceHeaders } from "@/hooks/use-access";
import { ApiClientError, apiRequest } from "@/lib/api";
import { useAuth } from "@/lib/auth/AuthContext";
import {
  clearReauthHandoff,
  reauthReturnTo,
  stashReauthHandoff,
  takeReauthHandoff,
} from "@/lib/auth/reauthHandoff";
import {
  clearStepUpAttempt,
  isMfaRequired,
  isStepUpRequired,
  noteStepUpAttempt,
  providerDidNotAskMessage,
  recentStepUpAttempt,
} from "@/lib/auth/mfaStepUp";
import { signInErrorCode } from "@/lib/auth/signInErrors";
import { dataLocationQueryKey } from "@/hooks/use-data-location";
import { logger } from "@/lib/logger";
import type { DbtSourceWarning } from "@shared/dbt/sourceWarnings";
import type { DestinationSummaryView, InstanceBindingView } from "@shared/types/instances";
import {
  BINDING_SECRET_FIELDS,
  isBindingSecretCopy,
  type BindingSecretField,
  type DbtProbeBody,
  type DbtProbeResult,
  type PlatformUpdateBindingBody,
  type UpdateBindingBody,
  type UpdateDbtSourceBody,
} from "@shared/types/instancesAdmin";

/**
 * The destination binding, and the privileged save behind it (FR-21, FR-14, SEC-4).
 *
 * The save is the only mutation in the app that can be refused for a reason the
 * user can fix WITHOUT losing what they were doing: `requireRecentAuth` answers
 * 401 `REAUTH_REQUIRED` when the token's `auth_time` is older than the window,
 * and nothing was written — no column, no vault entry. So the body is held here,
 * in a ref, rather than being re-derived from the form after the dialog closes:
 * re-deriving is what loses a field the user changed while the dialog was open,
 * and it is the one thing the acceptance test pins.
 *
 * `retryAfterReauth` replays the SAME body. It does not rebuild it, and it does
 * not ask the form for it again. A ref only survives while the document does,
 * though, and the MSAL provider authenticates by leaving the document, so the
 * same body plus the screen's draft go to `reauthHandoff` before the provider is
 * asked and come back on the other side of the redirect.
 */

export const BINDING_QUERY_KEY = "/api/v2/instances/current/binding";
/**
 * #923: the destination's readiness alone, unaudited, for every role: the banner and the run
 * buttons read it on every screen (the binding GET is an audited read).
 */
export const DESTINATION_READINESS_PATH = "/api/v2/instances/current/destination-readiness";
/**
 * #1106: the destination's type, whether it is set and its table name case, for any reader of
 * connections. The binding GET itself is Instance admin and above.
 */
export const DESTINATION_SUMMARY_PATH = "/api/v2/instances/current/destination-summary";
/** The transformation source (Session 10 P5): `dbt_jobs.write`, re-auth like the rest. */
export const DBT_SOURCE_PATH = "/api/v2/instances/current/binding/dbt";
/** Connect (Session 10 P5): a POST, never polled, audited as `binding.probe`. */
export const DBT_PROBE_PATH = "/api/v2/instances/current/binding/dbt/probe";

/** Any body a binding save can send; the path decides which. */
type SaveBody = UpdateBindingBody | UpdateDbtSourceBody | PlatformUpdateBindingBody;

/** B28: the PlatformAdmin route, the only one that takes a copy-mode secret field. */
export function platformBindingPath(instanceId: string): string {
  return `/api/v2/platform/instances/${encodeURIComponent(instanceId)}/binding`;
}

/**
 * The server's sentence for each secret field a refused save named (`secrets.<field>`), so the
 * screen can show it inline at that field (B28: a copy source that is missing or misnamed).
 */
export function secretFieldIssues(error: unknown): Partial<Record<BindingSecretField, string>> {
  const issues: Partial<Record<BindingSecretField, string>> = {};
  if (!(error instanceof ApiClientError)) return issues;
  for (const detail of error.details) {
    const field = detail.field?.startsWith("secrets.") ? detail.field.slice(8) : null;
    if (field && (BINDING_SECRET_FIELDS as readonly string[]).includes(field) && detail.issue) {
      issues[field as BindingSecretField] ??= detail.issue;
    }
  }
  return issues;
}

/** True when any secret field of the body is `{ copyFrom: { name } }` (B28). */
export function bodyHasSecretCopy(body: unknown): boolean {
  if (typeof body !== "object" || body === null) return false;
  const secrets = (body as { secrets?: unknown }).secrets;
  if (typeof secrets !== "object" || secrets === null) return false;
  return Object.values(secrets).some(isBindingSecretCopy);
}

/** Shown in the dialog when identity was not proved, so the button is not silent. */
const REAUTH_FAILED_MESSAGE =
  "We could not confirm your identity. Try again, or sign out and back in if this keeps happening.";

/** #1106: the Connections list's Destination column and the editor's table naming read this. */
export function useDestinationSummary() {
  const { selectedInstance } = useTenantInstance();
  return useQuery<DestinationSummaryView>({
    queryKey: instanceKey(DESTINATION_SUMMARY_PATH, selectedInstance?.id),
    enabled: Boolean(selectedInstance),
    queryFn: async ({ signal }) =>
      apiRequest<DestinationSummaryView>(DESTINATION_SUMMARY_PATH, {
        signal,
        headers: instanceHeaders(selectedInstance?.id),
      }),
  });
}

export function useBinding(enabled = true) {
  const { selectedInstance } = useTenantInstance();
  return useQuery<InstanceBindingView>({
    queryKey: instanceKey(BINDING_QUERY_KEY, selectedInstance?.id),
    enabled,
    // See `instanceHeaders` in `use-access.ts` for why the header is explicit.
    queryFn: async ({ signal }) =>
      apiRequest<InstanceBindingView>(BINDING_QUERY_KEY, {
        signal,
        headers: instanceHeaders(selectedInstance?.id),
      }),
  });
}

/**
 * A refusal the re-authentication dialog answers: `REAUTH_REQUIRED` (FR-14) or, with an MFA mode
 * on, `MFA_REQUIRED` (admin MFA). Nothing was written for either.
 */
export function isReauthRequired(error: unknown): boolean {
  return isStepUpRequired(error);
}

export interface BindingSaveState<TBody extends SaveBody = UpdateBindingBody> {
  save: (body: TBody) => Promise<InstanceBindingView | null>;
  retryAfterReauth: () => Promise<InstanceBindingView | null>;
  cancelReauth: () => void;
  /** True while the re-authentication dialog should be on screen (FR-14). */
  isReauthOpen: boolean;
  /** Why the last attempt to prove identity got nowhere, or null (FR-14). */
  reauthError: string | null;
  /** Admin MFA: which refusal the dialog answers, for its copy. */
  reauthCode: "REAUTH_REQUIRED" | "MFA_REQUIRED";
  /** The provider came back without a second factor for this save: no second redirect. */
  reauthBlocked: boolean;
  /** "Do not ask again on this browser" for the dialog; 0 days hides it. */
  reauthRemember: { days: number; account: string | null };
  isSaving: boolean;
  error: unknown;
  /**
   * #1674: what the last save answered beside the saved binding (a dbt repository save's
   * `REF_NOT_FOUND`), in the server's words. Empty until a save answers one; a new save clears it.
   */
  warnings: DbtSourceWarning[];
}

/**
 * What to carry across an identity-provider redirect, and where to put it back.
 *
 * The hook holds the request body; only the screen knows the shape of its own
 * draft, so the screen supplies both halves of the round trip.
 */
export interface BindingHandoff<TDraft> {
  /** The in-app path the user should land on again. */
  returnTo: string;
  snapshot: () => TDraft | null;
  restore: (draft: TDraft) => void;
}

export function useSaveBinding<
  TDraft = unknown,
  TBody extends SaveBody = UpdateBindingBody,
>(options: {
  onSaved?: (view: InstanceBindingView) => void;
  onError?: (error: unknown) => void;
  handoff?: BindingHandoff<TDraft>;
  /**
   * Where the PUT goes (Session 10 P5): the Warehouse save keeps the binding route, the
   * transformation source saves to `DBT_SOURCE_PATH`. Both answer the binding view.
   */
  path?: string;
}): BindingSaveState<TBody> {
  const path = options.path ?? BINDING_QUERY_KEY;
  const client = useQueryClient();
  const { account, reauthenticate } = useAuth();
  // The address, not the object: `account` is replaced on every sign-in and the
  // effect below must re-run when the PERSON changes, not when the reference does.
  const accountEmail = account?.email ?? null;
  const { selectedInstance, me } = useTenantInstance();
  const instanceId = selectedInstance?.id;
  const [isReauthOpen, setReauthOpen] = useState(false);
  const [reauthError, setReauthError] = useState<string | null>(null);
  const [reauthCode, setReauthCode] = useState<"REAUTH_REQUIRED" | "MFA_REQUIRED">(
    "REAUTH_REQUIRED",
  );
  const [reauthBlocked, setReauthBlocked] = useState(false);
  const mfaStatus = me?.mfa;
  const [error, setError] = useState<unknown>(null);
  const [warnings, setWarnings] = useState<DbtSourceWarning[]>([]);
  // The body the server refused. Held across the dialog so the retry is the
  // same write, not a second guess at what the user meant.
  const pending = useRef<TBody | null>(null);

  // `options` is a fresh object literal on every render of the calling screen,
  // so naming it as a dependency below would rebuild `save` every render and the
  // callback identity a child memo depends on would never hold. The callbacks
  // are read through this ref instead, which is always the latest ones.
  const latest = useRef(options);
  useLayoutEffect(() => {
    latest.current = options;
  });

  const { mutateAsync, isPending } = useMutation({
    // B28: a body that copies an existing vault secret goes to the PlatformAdmin route, the only
    // one that accepts `copyFrom`; the instance routes would refuse it. Only the platform-admin
    // path of the screen can stage a copy, and a replay after re-authentication is routed the
    // same way because the route is chosen from the body.
    mutationFn: async (body: TBody) =>
      apiRequest<InstanceBindingView>(
        bodyHasSecretCopy(body) && selectedInstance
          ? platformBindingPath(selectedInstance.id)
          : path,
        {
          method: "PUT",
          body,
          headers: instanceHeaders(selectedInstance?.id),
        },
      ),
  });

  const run = useCallback(
    async (body: TBody): Promise<InstanceBindingView | null> => {
      try {
        setWarnings([]);
        // #1674: the warnings belong to this answer, not to the binding the cache holds.
        const { warnings: answered, ...view } = await mutateAsync(body);
        setWarnings(answered ?? []);
        pending.current = null;
        setReauthOpen(false);
        setReauthError(null);
        setError(null);
        client.setQueryData(instanceKey(BINDING_QUERY_KEY, instanceId), view);
        if (view.readiness) {
          client.setQueryData(instanceKey(DESTINATION_READINESS_PATH, instanceId), view.readiness);
        }
        // #1106: the Connections list's Destination column reads the summary, not the binding.
        void client.invalidateQueries({
          queryKey: instanceKey(DESTINATION_SUMMARY_PATH, instanceId),
        });
        // #2491: Your data names the table rule and the place from the binding.
        await client.invalidateQueries({ queryKey: dataLocationQueryKey(instanceId ?? null) });
        latest.current.onSaved?.(view);
        return view;
      } catch (cause) {
        if (isReauthRequired(cause)) {
          // Nothing was written. Keep the body and ask for a fresher identity.
          pending.current = body;
          const mfa = isMfaRequired(cause);
          // Admin MFA, "No loop": the provider was just asked for the second step and came back
          // without one, so the dialog says why and offers no second redirect.
          const noLoop = mfa && recentStepUpAttempt(accountEmail);
          setReauthCode(mfa ? "MFA_REQUIRED" : "REAUTH_REQUIRED");
          setReauthBlocked(noLoop);
          if (noLoop) setReauthError(providerDidNotAskMessage(mfaStatus));
          setReauthOpen(true);
          logger.info("destination save needs a fresher sign-in");
          return null;
        }
        setError(cause);
        latest.current.onError?.(cause);
        return null;
      }
    },
    [client, mutateAsync, accountEmail, mfaStatus, instanceId],
  );

  // `run` is rebuilt whenever the mutation is, and the rehydrate below fires
  // once from an effect that must not be rebuilt with it.
  const runRef = useRef(run);
  useLayoutEffect(() => {
    runRef.current = run;
  });

  /**
   * The other side of the redirect (FR-14, B-4).
   *
   * Gated on the instance rather than on mount: the replay is an
   * instance-scoped write, and `selectedInstance` is null until `/api/v2/me`
   * resolves, so firing at mount would send the PUT with no `x-instance-id` and
   * be refused. `takeReauthHandoff` removes the entry before handing it over, so
   * the staged credential in it exists for one turn and the replay happens once
   * however often this effect runs. It is also keyed by the account that staged
   * it, so a second person signing in on the same tab is never handed the first
   * person's draft to replay under their own name, and by the instance it was
   * staged against, so a switch made on the way back is not written over.
   */
  useEffect(() => {
    if (!selectedInstance) return;
    const handoff = latest.current.handoff;
    if (!handoff) return;
    // Two savers live on the destination page (Warehouse and the transformation source), and
    // each replays only the save it staged: the entry's `returnTo` names the tab it came from,
    // so the other saver leaves it alone rather than sending its body to the wrong route.
    if (reauthReturnTo(accountEmail, selectedInstance.id) !== handoff.returnTo) return;
    const carried = takeReauthHandoff(accountEmail, selectedInstance.id);
    if (!carried) return;
    if (carried.draft !== null && carried.draft !== undefined) {
      handoff.restore(carried.draft as TDraft);
    }
    const body = carried.body as TBody;
    pending.current = body;
    logger.info("resuming the destination save held across a sign-in");
    void runRef.current(body);
  }, [accountEmail, selectedInstance]);

  const retryAfterReauth = useCallback(async () => {
    const body = pending.current;
    if (!body || reauthBlocked) {
      setReauthOpen(false);
      return null;
    }
    setReauthError(null);
    if (reauthCode === "MFA_REQUIRED") noteStepUpAttempt(accountEmail);

    // Held BEFORE the provider is asked: a redirecting provider leaves the
    // document inside `reauthenticate()`, so anything written after that call
    // is never written at all.
    const handoff = latest.current.handoff;
    if (handoff) {
      stashReauthHandoff({
        returnTo: handoff.returnTo,
        account: accountEmail,
        // The write is instance-scoped through a header the replay rebuilds
        // from whatever is selected then, so the entry names the instance it
        // was refused for and is dropped if the user has since switched.
        instanceId: selectedInstance?.id ?? null,
        draft: handoff.snapshot(),
        body,
      });
    }

    let outcome: Awaited<ReturnType<typeof reauthenticate>>;
    try {
      outcome = await reauthenticate();
    } catch (cause) {
      clearReauthHandoff();
      // Nobody was sent to the provider: the "just asked" marker must not block the next try.
      clearStepUpAttempt();
      // The code only (#2006): Microsoft's text names the directory and the resource.
      logger.warn("re-authentication could not be started", {
        "skydata.reason": signInErrorCode(cause) ?? "unknown",
      });
      setReauthError(REAUTH_FAILED_MESSAGE);
      return null;
    }

    // The browser is on its way to the identity provider; the entry it will need
    // when it comes back is the one thing that must survive this.
    if (outcome === "redirecting") return null;

    // Nothing left the document, so nothing should be left in storage either.
    clearReauthHandoff();
    if (outcome === "failed") {
      setReauthError(REAUTH_FAILED_MESSAGE);
      return null;
    }
    return run(body);
  }, [accountEmail, reauthenticate, run, selectedInstance, reauthBlocked, reauthCode]);

  const cancelReauth = useCallback(() => {
    // The form keeps its values; only the prompt goes away. The held body does
    // not survive it — a staged credential has no reason to sit in a ref once
    // the save it belonged to has been abandoned, and the next save rebuilds it.
    pending.current = null;
    clearReauthHandoff();
    setReauthError(null);
    setReauthBlocked(false);
    setReauthOpen(false);
  }, []);

  return {
    save: run,
    retryAfterReauth,
    cancelReauth,
    isReauthOpen,
    reauthError,
    reauthCode,
    reauthBlocked,
    reauthRemember: { days: mfaStatus?.rememberDays ?? 0, account: accountEmail },
    isSaving: isPending,
    error,
    warnings,
  };
}

/**
 * Connect (Session 10 P5): what the repository offers. A mutation, not a query: it runs when the
 * user presses Connect and never on a timer, because every call is an audited `binding.probe`
 * and may spend the stored token.
 */
export function useDbtProbe() {
  const { selectedInstance } = useTenantInstance();
  return useMutation({
    mutationFn: async (body: DbtProbeBody) =>
      apiRequest<DbtProbeResult>(DBT_PROBE_PATH, {
        method: "POST",
        body,
        headers: instanceHeaders(selectedInstance?.id),
      }),
  });
}
